import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Entity, stringifyEntityRef } from '@backstage/catalog-model';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { Box, FormControl, InputLabel, MenuItem, Select } from '@material-ui/core';

import {
  BitbucketProjectsResponse,
  bitbucketProjectsApiRef,
} from '../../utils/BitbucketProjectsApi';
import {
  useBitbucketProjects,
  validateBitbucketProject,
} from '../../utils/bitbucket';
import { PickerSelect } from '../PickerSelect';

export const BitbucketProjectPickerField = (
  props: FieldExtensionComponentProps<string>,
) => {
  const {
    onChange,
    schema: { title = 'Bitbucket Project', description = '' } = {},
    required,
    rawErrors,
    formContext,
    name,
    uiSchema,
  } = props;

  const configApi = useApi(configApiRef);
  const catalogApi = useApi(catalogApiRef);
  const bitbucketProjectsApi = useApi(bitbucketProjectsApiRef);

  const selectedBitbucketProject =
    (formContext?.formData?.[name] as string) || '';

  // Feature flag to enable new Bitbucket Projects API
  const featureFlagEnabled =
    configApi.getOptionalBoolean('configFeatureFlags.useBitbucketProjectsApi') ??
    false;

  // DO-4126: Support both workspace (string) and workspaces (array) for backward compatibility
  const workspaceConfig = uiSchema?.['ui:options']?.workspace as string | undefined;
  const workspacesConfig = uiSchema?.['ui:options']?.workspaces as string[] | undefined;

  // Determine if we have multiple workspaces or single workspace
  // Only show workspace selector when there are 2+ workspaces (better UX)
  const hasMultipleWorkspaces = Array.isArray(workspacesConfig) && workspacesConfig.length > 1;

  // DO-4126: Compute available workspaces (avoid nested ternary for lint compliance)
  const availableWorkspaces = useMemo(() => {
    if (hasMultipleWorkspaces) {
      return workspacesConfig;
    }
    if (workspacesConfig?.length === 1) {
      return workspacesConfig;
    }
    if (workspaceConfig) {
      return [workspaceConfig];
    }
    return [];
  }, [hasMultipleWorkspaces, workspacesConfig, workspaceConfig]);

  // Selected workspace state (for multiple workspaces mode)
  // DO-4126: Initialize from workspaces array if provided, otherwise from workspace string
  const [selectedWorkspace, setSelectedWorkspace] = useState<string | undefined>(() => {
    if (hasMultipleWorkspaces) {
      return availableWorkspaces[0];
    }
    // If workspaces array has one item, use it
    if (workspacesConfig?.length === 1) {
      return workspacesConfig[0];
    }
    // Otherwise use workspace string config
    return workspaceConfig;
  });

  // Effective workspace to use for API calls
  const workspace = selectedWorkspace || workspaceConfig || (availableWorkspaces.length > 0 ? availableWorkspaces[0] : undefined);

  // Regex filter from ui:options to filter projects by name pattern (e.g., "^PROJ-.*")
  const projectNameFilter = uiSchema?.['ui:options']?.projectNameFilter as string | undefined;

  // Use new API only if feature flag is enabled AND workspace is provided
  // Falls back to catalog if flag is disabled or workspace is missing
  const useBitbucketProjectsApi = featureFlagEnabled && !!workspace;

  // Graceful degradation: fallback to catalog when API is unavailable
  const [useCatalogFallback, setUseCatalogFallback] = useState(false);
  const [fallbackWarning, setFallbackWarning] = useState<string | undefined>(undefined);

  // Manual cache refresh: increment refreshTrigger to bypass cache and fetch fresh data
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Project validation: track validation errors and background validation state
  const [validationError, setValidationError] = useState<string | undefined>(undefined);
  const [isValidatingInBackground, setIsValidatingInBackground] = useState(false);
  const [lastValidatedProject, setLastValidatedProject] = useState<string | null>(null);

  const {
    projects: apiProjects,
    loading: apiLoading,
    error: apiError,
  } = useBitbucketProjects(workspace, refreshTrigger);

  const [catalogOptions, setCatalogOptions] = useState<
    Array<{ value: string; label: string; description?: string }>
  >([]);
  const [catalogLoading, setCatalogLoading] = useState<boolean>(false);
  const [catalogError, setCatalogError] = useState<Error | undefined>(undefined);

  useEffect(() => {
    // DO-4126: Only fetch catalog if:
    // 1. API is disabled (useBitbucketProjectsApi is false), OR
    // 2. Catalog fallback is explicitly enabled (useCatalogFallback is true)
    // Don't fetch catalog for workspace-specific errors (404/not found)
    if (useBitbucketProjectsApi && !useCatalogFallback) {
      return () => {};
    }

    let isMounted = true;
    const fetchCatalogProjects = async () => {
      setCatalogLoading(true);
      setCatalogError(undefined);
      try {
        const { items } = await catalogApi.getEntities({
          filter: {
            kind: 'Resource',
            'spec.type': 'bitbucketcloud-project',
          },
        });

        if (isMounted) {
          const options = items.map((entity: Entity) => ({
            value: stringifyEntityRef(entity),
            label: entity.metadata.title || entity.metadata.name,
            description: entity.metadata.description,
          }));
          setCatalogOptions(options);
        }
      } catch (e: any) {
        if (isMounted) {
          setCatalogError(e);
        }
      } finally {
        if (isMounted) {
          setCatalogLoading(false);
        }
      }
    };

    fetchCatalogProjects();
    return () => {
      isMounted = false;
    };
  }, [catalogApi, useBitbucketProjectsApi, useCatalogFallback, refreshTrigger]);

  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false);
  const [hasValidatedSelectedProject, setHasValidatedSelectedProject] = useState(false);
  const [initialSelectedProject, setInitialSelectedProject] = useState<string | null>(null);

  // Helper to determine error type
  const getErrorType = useCallback((err: any): 'network' | 'server' | 'timeout' | 'auth' | 'notFound' | 'unknown' => {
    // DO-4126: Check status code from multiple possible locations (status, statusCode, response.status)
    const status = err.status || err.statusCode || err.response?.status;

    if (status === 404) return 'notFound';
    if (status === 401 || status === 403) return 'auth';
    if (status >= 500 && status < 600) return 'server';

    // Check error message patterns
    const errorMessage = err.message?.toLowerCase() || '';
    const isNetworkError =
      errorMessage.includes('network') ||
      errorMessage.includes('fetch') ||
      errorMessage.includes('failed to fetch') ||
      errorMessage.includes('econnrefused') ||
      err.code === 'ECONNREFUSED' ||
      err.code === 'ETIMEDOUT' ||
      err.name === 'NetworkError';

    // DO-4126: Enhanced notFound detection - check for workspace-related errors
    // Note: status === 404 is already checked above, so only check error message patterns here
    const isNotFound =
      errorMessage.includes('not found') ||
      errorMessage.includes('404') ||
      (errorMessage.includes('workspace') && (errorMessage.includes('not found') || errorMessage.includes('doesn\'t exist'))) ||
      (errorMessage.includes('workspace') && errorMessage.includes('you don\'t have access'));

    const isTimeout = errorMessage.includes('timeout') || err.code === 'ETIMEDOUT';

    if (isNetworkError) return 'network';
    if (isTimeout) return 'timeout';
    if (isNotFound) return 'notFound';
    return 'unknown';
  }, []);

  // Graceful degradation: fallback to catalog on API errors
  // DO-4126: Don't fallback to catalog for workspace-specific errors (404/not found)
  // Only fallback for transient errors (network, server, timeout)
  useEffect(() => {
    if (!useBitbucketProjectsApi) {
      return;
    }

    if (apiError) {
      const errorType = getErrorType(apiError);

      // Only fallback to catalog for transient errors
      // Don't fallback for workspace-specific errors (404/not found) - show error instead
      if (errorType === 'network' || errorType === 'server' || errorType === 'timeout') {
        // eslint-disable-next-line no-console
        console.warn('API unavailable, falling back to catalog', {
          error: apiError.message,
          errorType,
        });
        setUseCatalogFallback(true);
        setFallbackWarning('Bitbucket API unavailable. Showing projects from catalog. Some projects may be outdated.');
      } else if (errorType === 'notFound') {
        // Workspace doesn't exist or invalid - show error, don't fallback to catalog
        // DO-4126: Explicitly disable catalog fallback for workspace-specific errors
        setUseCatalogFallback(false);
        setFallbackWarning(undefined);
        // Clear catalog options when workspace not found (don't show stale catalog data)
        setCatalogOptions([]);
        // eslint-disable-next-line no-console
        console.warn('Workspace not found or invalid', {
          error: apiError.message,
          workspace: selectedWorkspace,
          errorType,
        });
      } else {
        // Unknown error - don't fallback to catalog by default
        setUseCatalogFallback(false);
        setFallbackWarning(undefined);
        // Clear catalog options for unknown errors too
        setCatalogOptions([]);
      }
    } else {
      // No error - reset fallback state
      setUseCatalogFallback(false);
      setFallbackWarning(undefined);
      // Clear catalog options when API is working (use API data only)
      if (useBitbucketProjectsApi) {
        setCatalogOptions([]);
      }
    }
  }, [useBitbucketProjectsApi, apiError, getErrorType, selectedWorkspace]);

  const effectiveUseCatalog = !useBitbucketProjectsApi || useCatalogFallback;
  const loading = effectiveUseCatalog ? catalogLoading : apiLoading;
  const error = effectiveUseCatalog ? catalogError : apiError;

  useEffect(() => {
    if (!loading && isRefreshing) {
      setIsRefreshing(false);
    }
  }, [loading, isRefreshing]);

  // Helper to check if error is retryable
  const isRetryableError = useCallback((err: any): boolean => {
    const errorType = getErrorType(err);
    return errorType === 'network' || errorType === 'server' || errorType === 'timeout';
  }, [getErrorType]);

  // Retry helper with exponential backoff for transient API failures
  // Retries network/server/timeout errors with increasing delays (1s, 2s, 4s)
  const retryWithBackoff = useCallback(async <T,>(
    fn: () => Promise<T>,
    maxRetries: number = 3,
    delays: number[] = [1000, 2000, 4000]
  ): Promise<T> => {
    let lastError: any;

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        return await fn();
      } catch (err: any) {
        lastError = err;

        if (!isRetryableError(err) || attempt === maxRetries - 1) {
          throw err;
        }

        const delay = delays[attempt] || delays[delays.length - 1];
        // eslint-disable-next-line no-console
        console.log(`Retry attempt ${attempt + 1}/${maxRetries} after ${delay}ms`, {
          error: err.message,
          retryable: isRetryableError(err),
        });
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }

    throw lastError;
  }, [isRetryableError]);

  // Handle validation error based on error type
  const handleValidationError = useCallback((err: any, projectValue: string) => {
    const errorType = getErrorType(err);

    // eslint-disable-next-line no-console
    console.error('Background validation error', {
      error: err.message,
      selected: projectValue,
      errorStatus: err.status,
      errorCode: err.code,
      errorType,
    });

    if (errorType === 'network' || errorType === 'server' || errorType === 'timeout') {
      // Transient error: allow user to proceed with cached data (optimistic UI)
      // eslint-disable-next-line no-console
      console.warn('Validation failed due to transient API error - allowing user to proceed with cached data', {
        error: err.message,
        selected: projectValue,
        errorType,
      });
      setHasValidatedSelectedProject(true);
    } else if (errorType === 'auth') {
      // Auth error: show error message (critical, user needs to fix)
      const errorMsg = `Unable to validate project: Authentication failed. Please refresh the page.`;
      // eslint-disable-next-line no-console
      console.error('Validation failed due to authentication error', {
        error: err.message,
        selected: projectValue,
      });
      setValidationError(errorMsg);
      setHasValidatedSelectedProject(true);
    } else {
      // Unknown error: log but allow user to proceed (optimistic)
      // eslint-disable-next-line no-console
      console.warn('Validation failed with unknown error - allowing user to proceed', {
        error: err.message,
        selected: projectValue,
      });
      setHasValidatedSelectedProject(true);
    }
  }, [getErrorType]);

  // Reset selected workspace when config changes
  useEffect(() => {
    if (hasMultipleWorkspaces && availableWorkspaces.length > 0) {
      // If current selection is not in available workspaces, reset to first
      if (!selectedWorkspace || !availableWorkspaces.includes(selectedWorkspace)) {
        setSelectedWorkspace(availableWorkspaces[0]);
      }
    } else if (workspacesConfig?.length === 1) {
      // Single workspace in array: use the first (and only) workspace
      setSelectedWorkspace(workspacesConfig[0]);
    } else if (!hasMultipleWorkspaces && workspaceConfig) {
      // Single workspace mode: use the configured workspace string
      setSelectedWorkspace(workspaceConfig);
    }
  }, [hasMultipleWorkspaces, availableWorkspaces, selectedWorkspace, workspaceConfig, workspacesConfig]);

  // DO-4126: Reset error and fallback state when workspace changes
  // This ensures errors from previous workspace don't persist when switching workspaces
  useEffect(() => {
    // Reset fallback state when workspace changes (for multiple workspaces mode)
    if (hasMultipleWorkspaces && selectedWorkspace) {
      setUseCatalogFallback(false);
      setFallbackWarning(undefined);
      // Clear catalog options when workspace changes (don't show stale catalog data)
      setCatalogOptions([]);
    }
  }, [selectedWorkspace, hasMultipleWorkspaces]);

  // Background validation with optimistic UI: validate selected project without blocking UI
  // Fetches fresh data from API (bypassing cache) to verify project still exists
  const validateProjectInBackground = useCallback(async (projectValue: string): Promise<void> => {
    if (!useBitbucketProjectsApi || !workspace || !projectValue) {
      return;
    }

    if (isRefreshing || loading) {
      // eslint-disable-next-line no-console
      console.log('Skipping background validation - refresh already in progress', {
        selected: projectValue,
      });
      return;
    }

    if (lastValidatedProject === projectValue && isValidatingInBackground) {
      return;
    }

    setIsValidatingInBackground(true);
    setLastValidatedProject(projectValue);
    setValidationError(undefined);

    try {
      const data = await retryWithBackoff<BitbucketProjectsResponse>(
        () => bitbucketProjectsApi.getProjects(workspace, true),
        3,
        [1000, 2000, 4000]
      );

      const projectExists = validateBitbucketProject(projectValue, data.projects);

      if (projectExists) {
        // eslint-disable-next-line no-console
        console.log('Project validation successful - project exists', {
          selected: projectValue,
        });
        setValidationError(undefined);
        setHasValidatedSelectedProject(true);
      } else {
        // eslint-disable-next-line no-console
        console.error('Project validation failed - project not found in fresh data', {
          selected: projectValue,
          availableCount: data.projects.length,
        });

        const errorMsg = `Project "${projectValue}" no longer exists in workspace. Please select another project.`;
        setValidationError(errorMsg);
        setHasValidatedSelectedProject(true);
      }
    } catch (error_: any) {
      handleValidationError(error_, projectValue);
    } finally {
      setIsValidatingInBackground(false);
    }
  }, [useBitbucketProjectsApi, workspace, bitbucketProjectsApi, lastValidatedProject, isValidatingInBackground, retryWithBackoff, isRefreshing, loading, handleValidationError]);

  useEffect(() => {
    if (!loading && apiProjects.length > 0 && !hasInitiallyLoaded) {
      setHasInitiallyLoaded(true);
      if (selectedBitbucketProject && !initialSelectedProject) {
        setInitialSelectedProject(selectedBitbucketProject);
      }
    }
  }, [loading, apiProjects.length, hasInitiallyLoaded, selectedBitbucketProject, initialSelectedProject]);

  // Validate pre-selected values from template (default values)
  // Validates in background when component loads with a pre-selected project
  useEffect(() => {
    if (!useBitbucketProjectsApi) {
      return;
    }

    const isPreSelectedValue = initialSelectedProject &&
                               selectedBitbucketProject === initialSelectedProject;

    if (
      !isPreSelectedValue ||
      loading ||
      isRefreshing ||
      !selectedBitbucketProject ||
      apiProjects.length === 0 ||
      hasValidatedSelectedProject ||
      !hasInitiallyLoaded ||
      isValidatingInBackground
    ) {
      return;
    }

    const projectExists = validateBitbucketProject(selectedBitbucketProject, apiProjects);
    if (projectExists) {
      // eslint-disable-next-line no-console
      console.log('Pre-selected project found in cache - validating in background...', {
        selected: selectedBitbucketProject,
        cachedCount: apiProjects.length,
      });
      setHasValidatedSelectedProject(true);
      validateProjectInBackground(selectedBitbucketProject);
    }
  }, [
    useBitbucketProjectsApi,
    selectedBitbucketProject,
    initialSelectedProject,
    hasInitiallyLoaded,
    hasValidatedSelectedProject,
    loading,
    isRefreshing,
    apiProjects,
    isValidatingInBackground,
    validateProjectInBackground,
  ]);

  // Validate selected project after refresh completes
  // Prevents infinite refresh loop by validating only once after refresh
  useEffect(() => {
    if (!useBitbucketProjectsApi) {
      return;
    }

    if (
      loading ||
      isRefreshing ||
      !selectedBitbucketProject ||
      apiProjects.length === 0 ||
      hasValidatedSelectedProject
    ) {
      return;
    }

    const projectExists = validateBitbucketProject(selectedBitbucketProject, apiProjects);
    setHasValidatedSelectedProject(true);

    if (projectExists) {
      setValidationError(undefined);
    } else {
      const errorMsg = `Project "${selectedBitbucketProject}" no longer exists in workspace. Please select another project.`;
      // eslint-disable-next-line no-console
      console.error('Project validation failed - project not found after refresh', {
        selected: selectedBitbucketProject,
        availableCount: apiProjects.length,
      });
      setValidationError(errorMsg);
    }
  }, [useBitbucketProjectsApi, loading, isRefreshing, selectedBitbucketProject, apiProjects, hasValidatedSelectedProject]);

  // Reset validation state when project changes
  const prevSelectedProjectRef = useRef<string>('');
  useEffect(() => {
    if (prevSelectedProjectRef.current !== selectedBitbucketProject) {
      prevSelectedProjectRef.current = selectedBitbucketProject;

      setHasValidatedSelectedProject(false);
      setValidationError(undefined);
      setLastValidatedProject(null);

      if (selectedBitbucketProject && !initialSelectedProject && hasInitiallyLoaded) {
        setInitialSelectedProject(selectedBitbucketProject);
      } else if (!selectedBitbucketProject) {
        setInitialSelectedProject(null);
      }
    }
  }, [selectedBitbucketProject, initialSelectedProject, hasInitiallyLoaded]);

  // Optimistic UI: show selection immediately, validate in background
  // If project exists in cache → show immediately and validate in background
  // If project not in cache → refresh to get latest data, then validate
  const handleProjectChange = useCallback((newValue: string) => {
    onChange(newValue);
    setValidationError(undefined);

    if (effectiveUseCatalog || !newValue) {
      return;
    }

    const projectExistsInCache = validateBitbucketProject(newValue, apiProjects);

    if (projectExistsInCache && apiProjects.length > 0) {
      // Project in cache: show immediately, validate in background (non-blocking)
      // eslint-disable-next-line no-console
      console.log('Project found in cache - showing immediately, validating in background...', {
        selected: newValue,
        cachedCount: apiProjects.length,
      });

      setHasValidatedSelectedProject(true);
      validateProjectInBackground(newValue);
    } else {
      // Project not in cache: refresh to get latest data, then validate
      // eslint-disable-next-line no-console
      console.warn('Project not in cache - refreshing...', {
        selected: newValue,
        cachedCount: apiProjects.length,
      });
      setIsRefreshing(true);
      setRefreshTrigger(prev => prev + 1);
    }
  }, [onChange, effectiveUseCatalog, apiProjects, validateProjectInBackground]);

  const handleRefresh = useCallback(() => {
    setIsRefreshing(true);
    setRefreshTrigger(prev => prev + 1);
    setHasValidatedSelectedProject(false);
    setValidationError(undefined);
    setLastValidatedProject(null);
  }, []);

  // Transform projects to options format and apply regex filter if provided
  // Regex filter allows filtering projects by name pattern (e.g., "^PROJ-" matches all projects starting with "PROJ-")
  // DO-4126: Show workspace name in project labels when multiple workspaces are available
  const options = useMemo(() => {
    // DO-4126: Don't show any options if there's a 404 error (workspace not found)
    // Show empty options instead of stale data from previous workspace or catalog
    const hasNotFoundError = apiError && getErrorType(apiError) === 'notFound';

    if (hasNotFoundError) {
      // Return empty array for notFound errors - don't show stale data
      return [];
    }

    let baseOptions: Array<{ value: string; label: string; description?: string }>;

    if (effectiveUseCatalog) {
      baseOptions = catalogOptions;
    } else {
      baseOptions = apiProjects.map(project => {
        // When multiple workspaces are available, include workspace in label for clarity
        const label = hasMultipleWorkspaces && project.metadata?.workspace
          ? `${project.metadata.workspace} - ${project.label}`
          : project.label;
        return {
          value: project.value,
          label,
          description: project.metadata?.projectKey,
        };
      });
    }

    // Apply regex filter from ui:options.projectNameFilter if provided
    // Example: projectNameFilter: "^PROJ-" will show only projects starting with "PROJ-"
    if (projectNameFilter) {
      try {
        const regex = new RegExp(projectNameFilter);
        const filtered = baseOptions.filter(opt => regex.test(opt.label));
        // eslint-disable-next-line no-console
        console.log(`Applied regex filter "${projectNameFilter}": ${baseOptions.length} → ${filtered.length} projects`);
        return filtered;
      } catch (e) {
        // Invalid regex pattern: log error and return all options (graceful degradation)
        // eslint-disable-next-line no-console
        console.error('Invalid regex pattern in projectNameFilter:', projectNameFilter, e);
        return baseOptions;
      }
    }

    return baseOptions;
  }, [effectiveUseCatalog, apiProjects, catalogOptions, projectNameFilter, hasMultipleWorkspaces, apiError, getErrorType]);

  // NOTE: Invalid project values are handled as follows:
  // - Backend API only returns valid projects from Bitbucket, so it never returns invalid values
  // - Invalid values can occur from: (1) user manually entering invalid text, (2) stale form state
  //   (e.g., project deleted in Bitbucket but form still has old value), or (3) cached data
  // - Frontend validates selected project against current options list and shows "Selected project is not valid" error
  // - Form submission prevention is handled by the form framework (react-hook-form) when errorText is set
  // - Edge case: If validation fails due to transient API errors, we allow optimistic submission with cached data
  const isValidSelection =
    !selectedBitbucketProject ||
    (effectiveUseCatalog
      ? options.some(opt => opt.value === selectedBitbucketProject)
      : validateBitbucketProject(selectedBitbucketProject, apiProjects));

  // DO-4126: Show user-friendly error message for workspace not found
  const getErrorMessage = useCallback(() => {
    if (rawErrors?.[0]) return rawErrors[0];
    if (validationError) return validationError;

    // Don't show fallback warning for workspace-specific errors (404/not found)
    // Only show it for transient errors (network/server/timeout)
    if (error) {
      const errorType = getErrorType(error);
      if (errorType === 'notFound') {
        // Workspace-specific error: show clear message, don't show catalog fallback warning
        return `Workspace "${selectedWorkspace || workspace}" not found or has no projects. Please select a valid workspace.`;
      }
      // For other errors, show fallback warning if available
      if (fallbackWarning) return fallbackWarning;
      return error.message;
    }

    // Show fallback warning only if no error (catalog fallback for transient errors)
    if (fallbackWarning) return fallbackWarning;

    if (!isValidSelection && selectedBitbucketProject) {
      return 'Selected project is not valid';
    }

    return undefined;
  }, [rawErrors, validationError, fallbackWarning, error, getErrorType, selectedWorkspace, workspace, isValidSelection, selectedBitbucketProject]);

  const errorText = getErrorMessage();

  const loadingMessage = effectiveUseCatalog
    ? 'Loading projects from catalog...'
    : 'Fetching projects from Bitbucket...';

  // Handle workspace selection change (for multiple workspaces mode)
  const handleWorkspaceChange = useCallback((newWorkspace: string) => {
    setSelectedWorkspace(newWorkspace);
    // Reset project selection when workspace changes
    onChange('');
    setHasValidatedSelectedProject(false);
    setValidationError(undefined);
    setLastValidatedProject(null);
    setInitialSelectedProject(null);
    // DO-4126: Reset error and fallback state when workspace changes
    // This ensures errors from previous workspace (e.g., dummy workspace) don't persist
    setUseCatalogFallback(false);
    setFallbackWarning(undefined);
    // Clear catalog options when workspace changes (don't show stale catalog data)
    setCatalogOptions([]);
    // Reset refresh trigger to fetch new workspace projects
    setRefreshTrigger(prev => prev + 1);
  }, [onChange]);

  return (
    <Box>
      {/* DO-4126: Workspace selector (only shown when multiple workspaces are configured) */}
      {hasMultipleWorkspaces && (
        <Box marginBottom={2}>
          <FormControl fullWidth required={required}>
            <InputLabel id="bitbucket-workspace-selector-label">
              Bitbucket Workspace
            </InputLabel>
            <Select
              labelId="bitbucket-workspace-selector-label"
              id="bitbucket-workspace-selector"
              value={selectedWorkspace || ''}
              onChange={(e) => handleWorkspaceChange(e.target.value as string)}
              label="Bitbucket Workspace"
            >
              {availableWorkspaces.map((ws) => (
                <MenuItem key={ws} value={ws}>
                  {ws}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      <PickerSelect
        title={title}
        description={description}
        required={required}
        value={selectedBitbucketProject}
        onChange={effectiveUseCatalog ? onChange : handleProjectChange}
        loading={loading}
        loadingMessage={loadingMessage}
        errorText={errorText}
        options={options}
        labelId="bitbucket-project-picker-label"
        onRefresh={effectiveUseCatalog ? undefined : handleRefresh}
        refreshing={isRefreshing}
        enableSearch={!effectiveUseCatalog}
      />
    </Box>
  );
};
